import { useRef, useState } from 'react'
import { logError } from '../../util/errorLogger'
import AppIcon from '../icons/AppIcon'
import Overlay from './Overlay'
import styles from './FullscreenVideoOverlay.module.css'

export interface FullscreenVideoOverlayProps {
  videoUrl: string
  onClose: () => void
  dismissible?: boolean
  testId?: string
}

export default function FullscreenVideoOverlay({
  videoUrl,
  onClose,
  dismissible = false,
  testId,
}: FullscreenVideoOverlayProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function togglePlay() {
    const video = videoRef.current
    if (!video) return

    if (!video.paused) {
      video.pause()
      return
    }

    setError(null)
    try {
      if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        video.load()
        await new Promise<void>((resolve, reject) => {
          const onReady = () => {
            cleanup()
            resolve()
          }
          const onFail = () => {
            cleanup()
            reject(new Error('Video failed to load'))
          }
          const cleanup = () => {
            video.removeEventListener('canplay', onReady)
            video.removeEventListener('error', onFail)
          }
          video.addEventListener('canplay', onReady, { once: true })
          video.addEventListener('error', onFail, { once: true })
        })
      }
      await video.play()
    } catch (err) {
      setError('This video could not be played. Check your connection and try again.')
      void logError(err, { component: 'FullscreenVideoOverlay', action: 'play', videoUrl })
    }
  }

  return (
    <Overlay variant="fullscreen" onClose={dismissible ? onClose : undefined} ariaLabel="Video">
      <div className={styles.shell} data-testid={testId}>
        {dismissible && (
          <button type="button" className={styles.close} onClick={onClose} aria-label="Dismiss">
            <AppIcon icon="mdi:close" size={24} />
          </button>
        )}
        {!isPlaying && !error && (
          <button type="button" className={styles.play} onClick={() => void togglePlay()} aria-label="Play">
            <AppIcon icon="mdi:play" size={48} />
          </button>
        )}
        {error && (
          <div className={styles.error}>
            <p>{error}</p>
            <button type="button" className={styles.retry} onClick={() => void togglePlay()}>
              Try again
            </button>
          </div>
        )}
        <video
          ref={videoRef}
          className={styles.video}
          src={videoUrl}
          playsInline
          preload="metadata"
          controls={false}
          onEnded={onClose}
          onPlay={() => {
            setIsPlaying(true)
            setError(null)
          }}
          onPause={() => setIsPlaying(false)}
          onClick={() => void togglePlay()}
          onError={() => {
            setError('This video could not be loaded. Check your connection and try again.')
          }}
        />
      </div>
    </Overlay>
  )
}
